"use client";

import React, { useEffect, useRef, useState } from "react";
import { SecuredFooter } from "../ui/secured-footer";
import CreatePaymentFlow from "./CreatePaymentFlow";
import { DPOS_FIGMA_ASSETS } from "./dposFigmaAssets";
import DposLoadingCard from "./DposLoadingCard";
import {
  useDposCashierModeStatus,
  useExitDposCashierMode,
  useSetupDposCashierPin,
  useVerifyDposCashierPin,
} from "../../hooks/query/dposQueries";
import { useToast } from "../../hooks/use-toast";

type CashierModeStage =
  | "entry"
  | "setup"
  | "confirm"
  | "unlock"
  | "exit-unlock"
  | "active"
  | "create-payment";

interface CashierModeFlowProps {
  businessId: string | null;
  onExit: () => void;
}

interface CashierPasscodeCardProps {
  title: string;
  description: string;
  actionLabel: string;
  secondaryLabel: string;
  maxDigits: number;
  requiredDigits: number;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onSecondaryAction: () => void;
  isSubmitting: boolean;
  isSubmitDisabled: boolean;
  errorMessage: string | null;
}

function resolveCashierModeErrorMessage(message: string): string {
  const normalizedMessage = message.trim().toLowerCase();
  const referencesPasscode =
    normalizedMessage.includes("pin") || normalizedMessage.includes("passcode");

  if (normalizedMessage.includes("incorrect") && referencesPasscode) {
    return "Incorrect passcode. Please try again.";
  }

  if (normalizedMessage.includes("required") && referencesPasscode) {
    return "Enter your passcode to continue.";
  }

  return message;
}

function CashierModeIllustration() {
  return (
    <div className="relative h-[136px] w-[194px]">
      <div className="absolute left-[67px] top-[38px] h-[59px] w-[59px] rounded-full bg-[#F1EDF7]" />
      <div className="absolute left-[53px] top-[14px] h-[14px] w-[14px] rounded-full bg-[#EB3366]" />
      <img
        src={DPOS_FIGMA_ASSETS.passwordAsterisk}
        alt=""
        aria-hidden="true"
        className="absolute left-[78px] top-[44px] h-[48px] w-[38px]"
      />
      <img
        src={DPOS_FIGMA_ASSETS.passwordAsterisk}
        alt=""
        aria-hidden="true"
        className="absolute left-[10px] top-[44px] h-[48px] w-[38px]"
      />
      <img
        src={DPOS_FIGMA_ASSETS.passwordAsterisk}
        alt=""
        aria-hidden="true"
        className="absolute left-[146px] top-[44px] h-[48px] w-[38px]"
      />
      <img
        src={DPOS_FIGMA_ASSETS.passwordDot}
        alt=""
        aria-hidden="true"
        className="absolute left-[177px] top-[107px] h-[9px] w-[9px]"
      />
    </div>
  );
}

function CashierHomeIllustration() {
  return (
    <div className="relative h-[136px] w-[194px]">
      <div className="absolute left-[18px] top-[17px] h-[51px] w-[51px] rounded-full bg-[#F2E9FB]" />
      <img
        src={DPOS_FIGMA_ASSETS.cashierHomeDot}
        alt=""
        aria-hidden="true"
        className="absolute left-[88px] top-[17px] h-[9px] w-[9px]"
      />
      <img
        src={DPOS_FIGMA_ASSETS.cashierHomeStar}
        alt=""
        aria-hidden="true"
        className="absolute left-[136px] top-[79px] h-[29px] w-[29px]"
      />
      <img
        src={DPOS_FIGMA_ASSETS.cashierHomeFinance}
        alt=""
        aria-hidden="true"
        className="absolute left-[52px] top-[24px] h-[89px] w-[89px]"
      />
    </div>
  );
}

function CashierEntryIllustration() {
  return (
    <div className="relative h-[136px] w-[194px]">
      <div className="absolute left-[18px] top-[17px] h-[51px] w-[51px] rounded-full bg-[#F2E9FB]" />
      <img
        src={DPOS_FIGMA_ASSETS.cashierEntryDot}
        alt=""
        aria-hidden="true"
        className="absolute left-[88px] top-[17px] h-[9px] w-[9px]"
      />
      <img
        src={DPOS_FIGMA_ASSETS.cashierEntryStar}
        alt=""
        aria-hidden="true"
        className="absolute left-[136px] top-[79px] h-[29px] w-[29px]"
      />
      <img
        src={DPOS_FIGMA_ASSETS.cashierEntryFinance}
        alt=""
        aria-hidden="true"
        className="absolute left-[52px] top-[24px] h-[89px] w-[89px]"
      />
    </div>
  );
}

function CashierHomeCard({
  onCreatePayment,
  onExit,
}: {
  onCreatePayment: () => void;
  onExit: () => void;
}) {
  return (
    <div className="bg-white border border-[#EBE9EF] rounded-[24px] flex flex-col items-center w-full min-h-[790px] relative">
      <div className="flex flex-1 flex-col items-center justify-center gap-6 px-6 py-12 text-center w-full">
        <CashierHomeIllustration />
        <div className="flex flex-col gap-6 items-center">
          <h2 className="text-[24px] font-semibold leading-[28px] text-[#33125F]">
            Digital Point of Sale
          </h2>
          <p className="text-[14px] font-normal leading-[20px] text-[#423E51] max-w-[358px]">
            Create payment requests for your customers
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <button
            onClick={onExit}
            className="w-[160px] rounded-full border border-[#EBE9EF] h-[48px] text-[14px] font-semibold text-[#6D6584] transition-colors hover:bg-[#F5F4F7] disabled:cursor-not-allowed disabled:bg-[#F5F4F7] disabled:text-[#9D96AF]"
          >
            Exit
          </button>
          <button
            onClick={onCreatePayment}
            className="w-[160px] rounded-full h-[48px] text-[14px] font-semibold text-white hover:opacity-90 transition-opacity"
            style={{
              backgroundImage:
                "linear-gradient(77deg, #57249C 10.66%, #EB3366 113.47%)",
            }}
          >
            Create Payment
          </button>
        </div>
      </div>
      <SecuredFooter className="w-full p-6 justify-center border-t border-[#EBE9EF]" />
    </div>
  );
}

function CashierFullscreenPage({ children }: { children: React.ReactNode }) {
  return (
    <div
      data-testid="dpos-cashier-fullscreen"
      className="fixed inset-0 z-[1000] overflow-y-auto bg-[#F8F8F8]"
    >
      <div className="mx-auto flex min-h-screen w-full max-w-[1440px] items-stretch p-0 sm:p-6">
        {children}
      </div>
    </div>
  );
}

function CashierHelpFooter() {
  return (
    <div className="flex w-full justify-center border-t border-[#EBE9EF] px-4 py-4">
      <button className="text-[16px] font-semibold leading-[24px] text-[#EB3366]">
        Need help?
      </button>
    </div>
  );
}

function CashierPasscodeCard({
  title,
  description,
  actionLabel,
  secondaryLabel,
  maxDigits,
  requiredDigits,
  value,
  onChange,
  onSubmit,
  onSecondaryAction,
  isSubmitting,
  isSubmitDisabled,
  errorMessage,
}: CashierPasscodeCardProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const hasError = Boolean(errorMessage);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className="bg-white border border-[#EBE9EF] rounded-[24px] flex flex-col items-center w-full min-h-[790px] relative">
      <div className="flex flex-1 flex-col items-center justify-center gap-6 w-full max-w-[794px] px-6 py-12">
        <CashierModeIllustration />
        <div className="flex flex-col gap-6 items-center text-center w-full">
          <h2 className="text-[24px] font-semibold leading-[28px] text-[#33125F]">
            {title}
          </h2>
          <p className="text-[14px] font-normal leading-[20px] text-[#423E51] max-w-[420px]">
            {description}
          </p>
        </div>

        <button
          type="button"
          onClick={() => inputRef.current?.focus()}
          className="relative flex items-center justify-center gap-[16px] rounded-[25px] focus:outline-none"
        >
          <input
            ref={inputRef}
            inputMode="numeric"
            autoComplete="one-time-code"
            aria-label={title}
            value={value}
            onChange={(event) =>
              onChange(
                event.target.value.replace(/\D/g, "").slice(0, maxDigits)
              )
            }
            onKeyDown={(event) => {
              if (event.key === "Enter" && value.length >= requiredDigits) {
                onSubmit();
              }
            }}
            className="pointer-events-none absolute opacity-0"
          />
          {Array.from({ length: maxDigits }).map((_, index) => (
            <div
              key={index}
              className={`flex h-[90px] w-[90px] items-center justify-center rounded-[30px] bg-white ${
                hasError ? "border border-[#EB3366]" : "border border-[#CECBD7]"
              }`}
            >
              <span className="text-[32px] font-semibold leading-[45px] text-[#6D6584]">
                {value.slice(index, index + 1) ? "•" : ""}
              </span>
            </div>
          ))}
        </button>

        {errorMessage ? (
          <p className="text-[14px] font-semibold leading-[20px] text-[#EB3366] text-center">
            {errorMessage}
          </p>
        ) : null}

        <div className="flex flex-col sm:flex-row items-center gap-4">
          <button
            onClick={onSecondaryAction}
            disabled={isSubmitting}
            className="w-[180px] rounded-full border border-[#EBE9EF] h-[48px] text-[14px] font-semibold text-[#6D6584] hover:bg-[#F5F4F7] transition-colors disabled:opacity-60"
          >
            {secondaryLabel}
          </button>
          <button
            onClick={onSubmit}
            disabled={isSubmitting || isSubmitDisabled}
            className="w-[180px] rounded-full h-[48px] text-[14px] font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-70"
            style={{
              backgroundImage:
                "linear-gradient(77deg, #57249C 10.66%, #EB3366 113.47%)",
            }}
          >
            {actionLabel}
          </button>
        </div>
      </div>
      <CashierHelpFooter />
    </div>
  );
}

export default function CashierModeFlow({
  businessId,
  onExit,
}: CashierModeFlowProps) {
  const { toast } = useToast();
  const [stage, setStage] = useState<CashierModeStage>("entry");
  const [draftPin, setDraftPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [entryPin, setEntryPin] = useState("");
  const [exitPin, setExitPin] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const statusQuery = useDposCashierModeStatus(businessId);
  const setupPinMutation = useSetupDposCashierPin(businessId);
  const verifyPinMutation = useVerifyDposCashierPin(businessId);
  const exitMutation = useExitDposCashierMode(businessId);

  const activeSessionId =
    statusQuery.data?.session?.id || statusQuery.data?.session?._id || null;
  const storedPinLength = statusQuery.data?.pinLength ?? 6;

  useEffect(() => {
    if (!businessId || statusQuery.isLoading) {
      return;
    }

    if (activeSessionId) {
      setStage((currentStage) =>
        currentStage === "create-payment" ? currentStage : "active"
      );
      return;
    }

    setStage((currentStage) =>
      currentStage === "create-payment" ? currentStage : "entry"
    );
  }, [activeSessionId, businessId, statusQuery.isLoading]);

  const resetPins = () => {
    setDraftPin("");
    setConfirmPin("");
    setEntryPin("");
    setExitPin("");
    setErrorMessage(null);
  };

  const handleSessionActivation = (sessionId: string | null) => {
    if (!sessionId) {
      setErrorMessage("Cashier mode session could not be started.");
      return;
    }

    setStage("active");
    resetPins();
  };

  const handleStart = () => {
    setErrorMessage(null);

    if (statusQuery.data?.isLocked) {
      setErrorMessage(
        "Cashier mode is temporarily locked. Please try again later."
      );
      return;
    }

    if (statusQuery.data?.hasPin) {
      setEntryPin("");
      setStage("unlock");
      return;
    }

    setStage("setup");
  };

  const handleVerifyForEntry = async (pin: string) => {
    const result = await verifyPinMutation.mutateAsync({ pin });
    const sessionId = result?.session?.id || result?.session?._id || null;

    if (!sessionId) {
      throw new Error("Cashier mode session could not be created.");
    }

    toast({ description: "Cashier mode unlocked." });
    handleSessionActivation(sessionId);
  };

  const handleSetupPin = async () => {
    if (draftPin.length < 4 || draftPin.length > 6) {
      setErrorMessage("Create a passcode with 4 to 6 digits.");
      return;
    }

    if (confirmPin !== draftPin) {
      setConfirmPin("");
      setErrorMessage("Passcodes do not match. Please try again.");
      return;
    }

    setErrorMessage(null);

    try {
      await setupPinMutation.mutateAsync({
        pin: draftPin,
        confirmPin,
      });

      toast({ description: "Cashier mode PIN created successfully." });
      await handleVerifyForEntry(draftPin);
    } catch (error: unknown) {
      setErrorMessage(
        error instanceof Error
          ? resolveCashierModeErrorMessage(error.message)
          : "Could not create the cashier mode PIN."
      );
    }
  };

  const handleDismissCashierMode = async (pin: string) => {
    setErrorMessage(null);

    try {
      await exitMutation.mutateAsync({ pin });
      resetPins();
      toast({ description: "Cashier mode exited." });
      onExit();
    } catch (error: unknown) {
      const description =
        error instanceof Error
          ? resolveCashierModeErrorMessage(error.message)
          : "Could not exit cashier mode.";

      setErrorMessage(description);
      toast({ description });
    }
  };

  if (!businessId || statusQuery.isLoading) {
    return <DposLoadingCard />;
  }

  if (stage === "create-payment") {
    return (
      <CashierFullscreenPage>
        <CreatePaymentFlow
          onClose={() => setStage("active")}
          onComplete={() => setStage("active")}
        />
      </CashierFullscreenPage>
    );
  }

  if (stage === "active") {
    return (
      <CashierFullscreenPage>
        <CashierHomeCard
          onCreatePayment={() => setStage("create-payment")}
          onExit={() => {
            setExitPin("");
            setErrorMessage(null);
            setStage("exit-unlock");
          }}
        />
      </CashierFullscreenPage>
    );
  }

  if (stage === "setup") {
    return (
      <CashierPasscodeCard
        title="Create cashier PIN"
        description="Create a 4 to 6 digit passcode to protect cashier mode."
        actionLabel="Enter cashier mode"
        secondaryLabel="Cancel"
        maxDigits={6}
        requiredDigits={4}
        value={draftPin}
        onChange={(value) => {
          setDraftPin(value.slice(0, 6));
          setErrorMessage(null);
        }}
        onSubmit={() => {
          if (draftPin.length < 4 || draftPin.length > 6) {
            setErrorMessage("Create a passcode with 4 to 6 digits.");
            return;
          }

          setConfirmPin("");
          setErrorMessage(null);
          setStage("confirm");
        }}
        onSecondaryAction={() => {
          resetPins();
          setStage("entry");
        }}
        isSubmitting={false}
        isSubmitDisabled={draftPin.length < 4 || draftPin.length > 6}
        errorMessage={errorMessage}
      />
    );
  }

  if (stage === "confirm") {
    return (
      <CashierPasscodeCard
        title="Confirm cashier PIN"
        description="Re-enter your passcode so we can finish setting up cashier mode."
        actionLabel="Enter cashier mode"
        secondaryLabel="Cancel"
        maxDigits={6}
        requiredDigits={draftPin.length}
        value={confirmPin}
        onChange={(value) => {
          setConfirmPin(value.slice(0, draftPin.length));
          setErrorMessage(null);
        }}
        onSubmit={() => {
          void handleSetupPin();
        }}
        onSecondaryAction={() => {
          resetPins();
          setStage("entry");
        }}
        isSubmitting={setupPinMutation.isPending || verifyPinMutation.isPending}
        isSubmitDisabled={confirmPin.length !== draftPin.length}
        errorMessage={errorMessage}
      />
    );
  }

  if (stage === "unlock") {
    return (
      <CashierPasscodeCard
        title="Cashier mode"
        description={`Please enter your ${storedPinLength}-digit passcode to enter cashier mode`}
        actionLabel="Enter cashier mode"
        secondaryLabel="Cancel"
        maxDigits={6}
        requiredDigits={storedPinLength}
        value={entryPin}
        onChange={(value) => {
          setEntryPin(value.slice(0, storedPinLength));
          setErrorMessage(null);
        }}
        onSubmit={() => {
          if (entryPin.length !== storedPinLength) {
            setErrorMessage(
              `Enter your ${storedPinLength}-digit passcode to continue.`
            );
            return;
          }

          setErrorMessage(null);
          void handleVerifyForEntry(entryPin).catch((error: unknown) => {
            setEntryPin("");
            setErrorMessage(
              error instanceof Error
                ? resolveCashierModeErrorMessage(error.message)
                : "Could not verify the cashier mode PIN."
            );
          });
        }}
        onSecondaryAction={() => {
          setEntryPin("");
          setErrorMessage(null);
          setStage("entry");
        }}
        isSubmitting={verifyPinMutation.isPending}
        isSubmitDisabled={entryPin.length !== storedPinLength}
        errorMessage={errorMessage}
      />
    );
  }

  if (stage === "exit-unlock") {
    return (
      <CashierFullscreenPage>
        <CashierPasscodeCard
          title="Exit cashier mode"
          description={`Please enter your ${storedPinLength}-digit passcode to exit cashier mode`}
          actionLabel={
            exitMutation.isPending ? "Exiting..." : "Exit cashier mode"
          }
          secondaryLabel="Back"
          maxDigits={6}
          requiredDigits={storedPinLength}
          value={exitPin}
          onChange={(value) => {
            setExitPin(value.slice(0, storedPinLength));
            setErrorMessage(null);
          }}
          onSubmit={() => {
            if (exitPin.length !== storedPinLength) {
              setErrorMessage(
                `Enter your ${storedPinLength}-digit passcode to continue.`
              );
              return;
            }

            setErrorMessage(null);
            void handleDismissCashierMode(exitPin).catch((error: unknown) => {
              setExitPin("");
              setErrorMessage(
                error instanceof Error
                  ? resolveCashierModeErrorMessage(error.message)
                  : "Could not exit cashier mode."
              );
            });
          }}
          onSecondaryAction={() => {
            setExitPin("");
            setErrorMessage(null);
            setStage("active");
          }}
          isSubmitting={exitMutation.isPending}
          isSubmitDisabled={exitPin.length !== storedPinLength}
          errorMessage={errorMessage}
        />
      </CashierFullscreenPage>
    );
  }

  return (
    <div className="bg-white border border-[#EBE9EF] rounded-[24px] flex flex-col items-center w-full min-h-[790px] relative">
      <div className="flex flex-1 flex-col items-center justify-center gap-6 px-6 py-12 text-center w-full">
        <CashierEntryIllustration />
        <div className="flex flex-col gap-6 items-center">
          <h2 className="text-[24px] font-semibold leading-[28px] text-[#33125F]">
            Cashier mode
          </h2>
          <p className="text-[14px] font-normal leading-[20px] text-[#423E51] max-w-[358px]">
            You are about to enter cashier mode. You will be required to enter a
            passcode to exit this mode.
          </p>
          {errorMessage ? (
            <p className="text-[14px] font-semibold leading-[20px] text-[#EB3366]">
              {errorMessage}
            </p>
          ) : null}
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <button
            onClick={() => {
              resetPins();
              onExit();
            }}
            className="w-[180px] rounded-full border border-[#EBE9EF] h-[48px] text-[14px] font-semibold text-[#6D6584] hover:bg-[#F5F4F7] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleStart}
            className="w-[180px] rounded-full h-[48px] text-[14px] font-semibold text-white hover:opacity-90 transition-opacity"
            style={{
              backgroundImage:
                "linear-gradient(77deg, #57249C 10.66%, #EB3366 113.47%)",
            }}
          >
            Enter cashier mode
          </button>
        </div>
      </div>
      <SecuredFooter className="w-full p-6 justify-center border-t border-[#EBE9EF]" />
    </div>
  );
}
